import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import { legacyCustomerApi, getApiErrorMessage } from '../subscriptionAdmin/api';
import {
  filterSubscriptionCustomers,
  planPaymentStatus,
  planNameLabel,
  billingProfileSummary,
} from '../subscriptionAdmin/userDisplay';

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const users = await legacyCustomerApi.listUsers();
      setCustomers(filterSubscriptionCustomers(users));
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copyId = (id) => {
    navigator.clipboard.writeText(String(id));
    setSnackbar({ open: true, type: 'success', message: 'User ID copied — use in Step 2 (Contracts).' });
  };

  const filtered = customers.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (c.email || '').toLowerCase().includes(q) ||
      (c.name || '').toLowerCase().includes(q) ||
      (c.organizationName || '').toLowerCase().includes(q) ||
      String(c._id).toLowerCase().includes(q)
    );
  });

  return (
    <PageShell
      title="Step 0 — Subscription customers"
      subtitle="Read-only list from GET /user-details · Create users on the existing Create User page"
      actions={
        <Button
          variant="contained"
          component={RouterLink}
          to="/create-user"
          startIcon={<OpenInNewIcon />}
        >
          Create user
        </Button>
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Customers are created on{' '}
        <Button size="small" component={RouterLink} to="/create-user">
          /create-user
        </Button>{' '}
        with <strong>purchaseType: subscriptionuser</strong>. This step only lists them for contracts and payment — no duplicate create form here.
      </Alert>

      <TextField
        size="small"
        placeholder="Search email, name, organization, or user ID…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2, maxWidth: 480 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Plan snapshot</TableCell>
                <TableCell>Plan status</TableCell>
                <TableCell>Billing (GST)</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell align="right">Use in pipeline</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((c) => (
                  <TableRow key={c._id} hover>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>{c.name || '—'}</TableCell>
                    <TableCell>{c.organizationName || '—'}</TableCell>
                    <TableCell>{c.phoneNumber || '—'}</TableCell>
                    <TableCell>{planNameLabel(c)}</TableCell>
                    <TableCell>
                      <Chip label={planPaymentStatus(c)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography variant="body2" noWrap title={billingProfileSummary(c)}>
                        {billingProfileSummary(c)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {String(c._id).slice(-12)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Copy full user ID">
                        <IconButton size="small" onClick={() => copyId(c._id)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        onClick={() =>
                          navigate('/admin-subscriptions/contracts', {
                            state: { prefillUserId: c._id },
                          })
                        }
                      >
                        Contract
                      </Button>
                    </TableCell>
                  </TableRow>
              ))}
              {!filtered.length && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#64748b' }}>
                    {customers.length === 0 ? (
                      <>
                        No subscription customers yet.{' '}
                        <Button component={RouterLink} to="/create-user" size="small">
                          Create a subscription user
                        </Button>
                      </>
                    ) : (
                      'No matches for your search.'
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </PageShell>
  );
}
